import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Users as UsersIcon, KeyRound, UserX, UserCheck, Search, RefreshCw, ShieldCheck } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { Avatar } from '@/shared/components/ui/Avatar';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { useUser } from '@/shared/hooks/useAuth';
import { tierOf } from '@/shared/permissions/identity';
import { useAdminUsers, useUserMutations, useLeaves } from '../hooks/useHr';
import { HrStatCards } from '../components/HrStatCards';
import { CreateUserModal } from '../components/CreateUserModal';
import { ResetPasswordModal } from '../components/ResetPasswordModal';
import { EmployeeDrawer } from '../components/EmployeeDrawer';
import { DEPT_LABELS, authorityLabel, authorityTone, fmtDate } from '../constants';
import type { HrUser, CreateUserPayload } from '../types';

const DEPT_FILTER_OPTIONS = [
  { value: 'all', label: 'All Departments' },
  { value: 'sales', label: 'Sales' },
  { value: 'hr', label: 'HR' },
  { value: 'it', label: 'IT' },
  { value: 'digital_marketing', label: 'Digital Mktg' },
  { value: 'management', label: 'Management' },
];

const TIER_FILTER_OPTIONS = [
  { value: 'all', label: 'All Tiers' },
  { value: 'SUPER_ADMIN', label: 'Executive' },
  { value: 'ADMIN', label: 'Administrator' },
  { value: 'EMPLOYEE', label: 'Employee' },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
];

export function HrPage() {
  const user = useUser();
  const isSuperAdmin = user?.role === 'superadmin';
  const myDept = user?.department ?? 'sales';

  const { data: users = [], isLoading, isError, isFetching, refetch } = useAdminUsers();
  const { data: leaves = [] } = useLeaves();
  const { createUser, updateUser, setUserRole } = useUserMutations();

  const [createOpen, setCreateOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<HrUser | null>(null);
  const [disableTarget, setDisableTarget] = useState<HrUser | null>(null);
  const [selected, setSelected] = useState<HrUser | null>(null);

  // Toolbar filter state
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // ─── HR stat cards (mirror legacy loadHrHub) ───────────────────────────────
  const stats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const staff = users.filter((u) => u.role !== 'superadmin');
    const onLeave = leaves.filter(
      (l) => l.status === 'approved' && l.from_date <= today && l.to_date >= today,
    ).length;
    return {
      totalStaff: staff.length,
      pendingLeaves: leaves.filter((l) => l.status === 'pending').length,
      onLeave,
      activeStaff: Math.max(0, staff.filter((u) => u.active).length - onLeave),
    };
  }, [users, leaves]);

  // Executive-only authority-tier distribution (derived from real data)
  const tierDist = useMemo(() => {
    let exec = 0;
    let admin = 0;
    let employee = 0;
    for (const u of users) {
      const t = tierOf(u.role);
      if (t === 'SUPER_ADMIN') exec++;
      else if (t === 'ADMIN') admin++;
      else employee++;
    }
    return { exec, admin, employee };
  }, [users]);

  // Backend authorization: admin may modify own-dept employees or self; superadmin any.
  const canModify = (u: HrUser) =>
    isSuperAdmin || u.id === user?.id || (u.role !== 'admin' && u.role !== 'superadmin');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (deptFilter !== 'all' && u.department !== deptFilter) return false;
      if (tierFilter !== 'all' && tierOf(u.role) !== tierFilter) return false;
      if (statusFilter === 'active' && !u.active) return false;
      if (statusFilter === 'disabled' && u.active) return false;
      if (q) {
        const hay = [u.display_name, u.username, DEPT_LABELS[u.department ?? ''] ?? u.department, authorityLabel(u.role)]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, search, deptFilter, tierFilter, statusFilter]);

  const handleCreate = (payload: CreateUserPayload) => {
    createUser.mutate(payload, {
      onSuccess: () => {
        toast.success('User created.');
        setCreateOpen(false);
      },
      onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to create user.'),
    });
  };

  const handleResetPassword = (password: string) => {
    if (!resetTarget) return;
    updateUser.mutate(
      { id: resetTarget.id, payload: { password } },
      {
        onSuccess: () => {
          toast.success('Password reset.');
          setResetTarget(null);
        },
        onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to reset password.'),
      },
    );
  };

  const handleChangeRole = (id: string, role: 'admin' | 'employee') => {
    setUserRole.mutate(
      { id, role },
      {
        onSuccess: () => toast.success(role === 'admin' ? 'Promoted to Administrator.' : 'Set to Employee.'),
        onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to change role.'),
      },
    );
  };

  const handleToggleActive = (u: HrUser, makeActive: boolean) => {
    updateUser.mutate(
      { id: u.id, payload: { active: makeActive } },
      {
        onSuccess: () => {
          toast.success(makeActive ? 'Account enabled.' : 'Account disabled.');
          setDisableTarget(null);
          setSelected(null);
        },
        onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to update account.'),
      },
    );
  };

  const renderRowActions = (u: HrUser) => {
    if (!canModify(u)) return <span className="text-[11px] text-on-surface-variant/50">—</span>;
    const isSelf = u.id === user?.id;
    return (
      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setResetTarget(u); }}>
          <KeyRound className="h-3.5 w-3.5" /> Reset
        </Button>
        {isSelf ? (
          <span className="text-[11px] text-on-surface-variant/50">(you)</span>
        ) : u.active ? (
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setDisableTarget(u); }}>
            <UserX className="h-3.5 w-3.5" /> Disable
          </Button>
        ) : (
          <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleToggleActive(u, true); }}>
            <UserCheck className="h-3.5 w-3.5" /> Enable
          </Button>
        )}
      </div>
    );
  };

  const columns: Column<HrUser>[] = [
    {
      key: 'name',
      header: 'Employee',
      render: (u) => (
        <div className="flex items-center gap-3 min-w-0">
          <Avatar name={u.display_name} size="sm" />
          <div className="min-w-0">
            <div className="font-medium text-on-surface">{u.display_name}</div>
            <div className="text-[11px] text-on-surface-variant">@{u.username}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'tier',
      header: 'Authority',
      render: (u) => (
        <Badge tone={authorityTone(u.role)}>
          <ShieldCheck className="h-3 w-3" /> {authorityLabel(u.role)}
        </Badge>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      render: (u) => <span className="text-on-surface-variant">{DEPT_LABELS[u.department ?? ''] ?? u.department ?? '—'}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (u) => (
        <div className="flex items-center gap-2">
          <Badge tone={u.active ? 'success' : 'danger'}>{u.active ? 'Active' : 'Disabled'}</Badge>
          {u.is_checked_in && <span className="text-[10px] text-success">● In</span>}
        </div>
      ),
    },
    { key: 'created', header: 'Joined', render: (u) => <span className="text-on-surface-variant">{fmtDate(u.created_at)}</span> },
    { key: 'leads', header: 'Leads', align: 'center', render: (u) => <span className="font-semibold tabular-nums">{u.lead_count ?? 0}</span> },
    { key: 'actions', header: 'Actions', align: 'right', render: renderRowActions },
  ];

  return (
    <div className="flex h-full flex-col gap-4 overflow-y-auto p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <UsersIcon className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-display-sm font-semibold text-on-surface">Team &amp; Access</h1>
            <p className="text-body-sm text-on-surface-variant">
              {isSuperAdmin ? 'Organization-wide team directory & access.' : "Manage your department's team & access."}
            </p>
          </div>
        </div>
        <Button onClick={() => setCreateOpen(true)} size="md">
          <Plus className="h-4 w-4" /> Create User
        </Button>
      </div>

      <HrStatCards {...stats} />

      {/* Executive-only authority distribution (CEO view) */}
      {isSuperAdmin && (
        <div className="flex flex-wrap items-center gap-2 text-body-sm">
          <span className="text-on-surface-variant">Authority distribution:</span>
          <Badge tone="primary">{tierDist.exec} Executive</Badge>
          <Badge tone="info">{tierDist.admin} Administrators</Badge>
          <Badge tone="neutral">{tierDist.employee} Employees</Badge>
        </div>
      )}

      {/* Enterprise toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[200px] flex-1">
          <Input
            name="user-search"
            placeholder="Search name, username, department…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
          />
        </div>
        {isSuperAdmin && (
          <div className="w-[170px]">
            <Select name="dept-filter" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} options={DEPT_FILTER_OPTIONS} />
          </div>
        )}
        <div className="w-[150px]">
          <Select name="tier-filter" value={tierFilter} onChange={(e) => setTierFilter(e.target.value)} options={TIER_FILTER_OPTIONS} />
        </div>
        <div className="w-[150px]">
          <Select name="status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} options={STATUS_FILTER_OPTIONS} />
        </div>
        <Button variant="outline" size="md" onClick={() => refetch()} loading={isFetching} aria-label="Refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : isError ? (
        <EmptyState title="Failed to load users" description="Check your connection or try refreshing." />
      ) : (
        <Table
          columns={columns}
          data={filtered}
          rowKey={(u) => u.id}
          onRowClick={(u) => setSelected(u)}
          empty={users.length === 0 ? 'No users in your department yet.' : 'No users match your filters.'}
        />
      )}

      <CreateUserModal
        open={createOpen}
        department={myDept}
        isSuperAdmin={isSuperAdmin}
        loading={createUser.isPending}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />

      <ResetPasswordModal
        open={!!resetTarget}
        userName={resetTarget?.display_name ?? ''}
        loading={updateUser.isPending}
        onClose={() => setResetTarget(null)}
        onSubmit={handleResetPassword}
      />

      <ConfirmDialog
        open={!!disableTarget}
        title="Disable Account"
        message={`Disable ${disableTarget?.display_name}'s account? They will be unable to log in.`}
        confirmLabel="Disable"
        danger
        loading={updateUser.isPending}
        onConfirm={() => disableTarget && handleToggleActive(disableTarget, false)}
        onCancel={() => setDisableTarget(null)}
      />

      <EmployeeDrawer
        member={selected}
        open={!!selected}
        canManageRole={!!selected && isSuperAdmin && selected.role !== 'superadmin' && selected.id !== user?.id}
        roleChanging={setUserRole.isPending}
        onChangeRole={handleChangeRole}
        onClose={() => setSelected(null)}
        actions={
          selected && canModify(selected) ? (
            <>
              <Button variant="outline" size="sm" onClick={() => { setResetTarget(selected); }}>
                <KeyRound className="h-3.5 w-3.5" /> Reset Password
              </Button>
              {selected.id !== user?.id &&
                (selected.active ? (
                  <Button variant="outline" size="sm" onClick={() => setDisableTarget(selected)}>
                    <UserX className="h-3.5 w-3.5" /> Disable
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" loading={updateUser.isPending} onClick={() => handleToggleActive(selected, true)}>
                    <UserCheck className="h-3.5 w-3.5" /> Enable
                  </Button>
                ))}
            </>
          ) : undefined
        }
      />
    </div>
  );
}
